import "../styles/notifications.less";

import styled from "@emotion/styled";
import type { PublicKey } from "@solana/web3.js";
import type { PendingReward } from "@stepfinance/liquidity";
import { ContextButton } from "@stepfinance/react";
import { notification } from "antd";
import type { ArgsProps } from "antd/lib/notification";
import type { ReactElement } from "react";

import { ReactComponent as ExternalLinkIcon } from "../assets/external-link.svg";
import { ReactComponent as FailIcon } from "../assets/notifications/failIcon.svg";
import { ReactComponent as InfoIcon } from "../assets/notifications/infoIcon.svg";
import { ReactComponent as PendingIcon } from "../assets/notifications/pendingIcon.svg";
import { ReactComponent as SuccessIcon } from "../assets/notifications/successIcon.svg";
import { ReactComponent as WarnIcon } from "../assets/notifications/warnIcon.svg";
import { ExplorerLink } from "../components/ExplorerLink/ExplorerLink";
import { TokenChangeNotificationDisplay } from "../components/Notifications/TokenChangeNotificationDisplay";

export type NotificationType = "success" | "error" | "info" | "warning" | "warn" | "open";

type NotifyProps = Partial<Pick<ArgsProps, "message" | "description" | "placement" | "duration" | "key" | "icon">> & {
  type: NotificationType;
  txId?: string;
};

type GenerateDescriptionTokenOption = {
  action: string;
  mint: string | PublicKey | string[] | PublicKey[];
  amount: number;
  type: "increase" | "decrease" | "equal";
};

type GenerateDescriptionOptions = {
  tokenA: GenerateDescriptionTokenOption;
  tokenB: GenerateDescriptionTokenOption;
};

const defaultIcons: { [type in NotificationType]: JSX.Element } = {
  error: <FailIcon />,
  info: <InfoIcon />,
  open: <PendingIcon />,
  success: <SuccessIcon />,
  warn: <WarnIcon />,
  warning: <WarnIcon />,
};

export const notify = ({
  message = "",
  description = undefined,
  txId = "",
  type = "info",
  placement = "bottomLeft",
  key,
  duration = 4.5,
  icon = undefined,
}: NotifyProps) => {
  let usedIcon;
  // Determine which icon to use in notifications
  if (icon) {
    usedIcon = icon;
  } else {
    usedIcon = defaultIcons[type];
  }
  return notification[type]({
    className: "notify-component",
    message: <span>{message}</span>,
    description:
      description || txId ? (
        <>
          {description && <DescriptionWrapper>{description}</DescriptionWrapper>}
          {txId && (
            <TransactionButtonWrapper>
              <NotificationTxnButton onClick={() => window.open(`https://solscan.io/tx/${txId}`, "_blank")}>
                View on Solscan
                <ViewExplorerIcon />
              </NotificationTxnButton>
            </TransactionButtonWrapper>
          )}
        </>
      ) : null,
    placement,
    key,
    duration,
    icon: usedIcon,
  });
};

export const generateOneToOneNotificationDescription = (options: GenerateDescriptionOptions) => {
  return (
    <>
      <span>You {options.tokenA.action}:</span>
      <TokenChangeNotificationDisplay
        amount={options.tokenA.amount}
        mintAddress={options.tokenA.mint}
        type={options.tokenA.type}
      />
      <span>You {options.tokenB.action}:</span>
      <TokenChangeNotificationDisplay
        amount={options.tokenB.amount}
        mintAddress={options.tokenB.mint}
        type={options.tokenB.type}
      />
    </>
  );
};

export const getMultipleRewardClaimsNotificationContent = (rewards: PendingReward[], common) => {
  const tokenClaimNotifications: ReactElement[] = [];
  for (let i = 0; i < rewards.length; i++) {
    const reward = rewards[i];
    if (reward.rewardBalance > 0) {
      tokenClaimNotifications.push(
        <TokenChangeNotificationDisplay
          amount={reward.rewardBalance}
          mintAddress={reward.rewardToken.address}
          type="increase"
        />,
      );
    }
  }
  const notifications = common.notifications;
  const youDid = notifications.tokenBalanceChanges.youDid;
  const claimed = notifications.youDidAction.claimed;
  return {
    inProgress: {
      message: common.notifications.currentlyClaimingRewards.message,
      description: common.notifications.transactionInProgress,
    },
    success: {
      message: common.notifications.finishedRewardClaim.message,
      description: (
        <>
          <span>{youDid.replace("{}", claimed)}:</span>
          {tokenClaimNotifications}
        </>
      ),
    },
  };
};

// TODO: Deprecate this once Step Farms & Pools are completely gone.
export const NotificationExplorerLinkDisplay: React.FC<{ tx: string }> = ({ tx }) => (
  <div className="explorer-link-display-wrapper">
    <ExplorerLink address={tx} type="tx">
      {"View Transaction"}
    </ExplorerLink>
  </div>
);

const DescriptionWrapper = styled.div`
  margin-top: 15px;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const TransactionButtonWrapper = styled.div`
  display: flex;
  flex-direction: row-reverse;
  width: 100%;
`;

const NotificationTxnButton = styled(ContextButton)`
  margin-top: 15px;
  gap: 8px;
  svg {
    margin-right: 0;
  }
`;

const ViewExplorerIcon = styled(ExternalLinkIcon)`
  width: 15px;
  height: 15px;
`;
